import { startOfWeek, addDays } from "date-fns";

/**
 * Gets YYYY-MM-DD string from a UTC ISO string in the target timezone.
 * This is the correct and reliable way to get the local date string.
 */
export function getLocalYMDFromUTC(
  utcISOString: string,
  timeZone: string
): string {
  if (!utcISOString) return "";
  const date = new Date(utcISOString);
  return date.toLocaleDateString("en-CA", { timeZone });
}

/**
 * Gets the current date in the studio's timezone
 */
export function getTodayInStudioTz(studioTimezone: string): Date {
  const now = new Date();
  const dateString = now.toLocaleDateString("en-CA", {
    timeZone: studioTimezone,
  });
  // This correctly creates a Date object at midnight in the studio's timezone
  return new Date(`${dateString}T00:00:00`);
}

/**
 * Gets YYYY-MM-DD string for a date in the studio's timezone, with optional offset in days
 */
export function getLocalDateString(timezone: string, offsetDays = 0): string {
  const today = getTodayInStudioTz(timezone);
  today.setDate(today.getDate() + offsetDays);
  // Use toLocaleDateString to ensure correct formatting
  return today.toLocaleDateString("en-CA");
}

/**
 * Gets the start of the current week (Monday) in the studio's timezone
 */
export function getWeekStartInStudioTz(studioTimezone: string): Date {
  const today = getTodayInStudioTz(studioTimezone);
  return startOfWeek(today, { weekStartsOn: 1 });
}

/**
 * Gets an array of dates for the week starting from the given date
 */
export function getWeekDays(startDate: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
}

/**
 * Gets the hour (0-23) from a UTC ISO string in the target timezone
 */
export function getLocalHourFromUTC(
  utcISOString: string,
  timeZone: string
): number {
  if (!utcISOString) return 0;
  const date = new Date(utcISOString);
  const hourString = date.toLocaleTimeString("en-GB", {
    timeZone,
    hour: "2-digit",
  });
  return parseInt(hourString, 10);
}

/**
 * Formats a time string to 12-hour AM/PM format
 */
export function formatTimeTo12Hour(timeStr: string): string {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
